import { message } from 'antd';

/**
 * 错误类型枚举
 */
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  BUSINESS_ERROR = 'BUSINESS_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * 错误信息接口
 */
export interface AppError {
  type: ErrorType;
  message: string;
  code?: string | number;
  details?: any;
  originalError?: any;
}

/**
 * 错误处理配置
 */
export interface ErrorHandlerConfig {
  showMessage?: boolean;
  redirectOnAuthError?: boolean;
  customHandler?: (error: AppError) => void;
}

/**
 * 错误处理工具类
 */
export class ErrorHandler {
  /**
   * 解析错误，将各种类型的错误转换为统一的 AppError 格式
   */
  static parseError(error: any): AppError {
    // 网络错误
    if (error.message?.includes('网络错误') || error.message?.includes('Network Error')) {
      return {
        type: ErrorType.NETWORK_ERROR,
        message: '网络连接失败，请检查您的网络设置',
        originalError: error,
      };
    }

    // 认证错误
    if (error.code === 1001001001 || error.message?.includes('未登录') || error.message?.includes('登录')) {
      return {
        type: ErrorType.AUTH_ERROR,
        message: '登录已过期，请重新登录',
        code: error.code,
        originalError: error,
      };
    }

    // 业务错误（有明确的错误信息）
    if (error.message && error.code !== undefined) {
      return {
        type: ErrorType.BUSINESS_ERROR,
        message: error.message,
        code: error.code,
        details: error.details,
        originalError: error,
      };
    }

    // 验证错误
    if (error.name === 'ValidationError' || error.message?.includes('验证')) {
      return {
        type: ErrorType.VALIDATION_ERROR,
        message: error.message || '数据验证失败',
        details: error.details,
        originalError: error,
      };
    }

    // 未知错误
    return {
      type: ErrorType.UNKNOWN_ERROR,
      message: error.message || '操作失败，请稍后重试',
      originalError: error,
    };
  }

  /**
   * 处理错误
   */
  static handleError(error: any, config: ErrorHandlerConfig = {}): AppError {
    const {
      showMessage = true,
      redirectOnAuthError = true,
      customHandler,
    } = config;

    // 解析错误
    const appError = this.parseError(error);

    // 自定义错误处理
    if (customHandler) {
      customHandler(appError);
      return appError;
    }

    // 显示错误消息
    if (showMessage) {
      message.error(appError.message);
    }

    // 认证错误重定向
    if (appError.type === ErrorType.AUTH_ERROR && redirectOnAuthError) {
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }

    // 记录错误日志
    this.logError(appError);

    return appError;
  }

  /**
   * 记录错误日志
   */
  static logError(error: AppError): void {
    console.error('应用错误:', {
      type: error.type,
      message: error.message,
      code: error.code,
      details: error.details,
      originalError: error.originalError,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });

    // 这里可以添加错误上报逻辑，如发送到监控系统
    // 例如：reportErrorToMonitoring(error);
  }

  /**
   * 处理 API 请求错误
   */
  static handleApiError(error: any, config: ErrorHandlerConfig = {}): AppError {
    return this.handleError(error, {
      ...config,
      showMessage: config.showMessage !== false,
    });
  }

  /**
   * 处理表单验证错误
   */
  static handleValidationError(error: any, config: ErrorHandlerConfig = {}): AppError {
    return this.handleError(error, {
      ...config,
      showMessage: config.showMessage !== false,
    });
  }

  /**
   * 处理业务逻辑错误
   */
  static handleBusinessError(error: any, config: ErrorHandlerConfig = {}): AppError {
    return this.handleError(error, {
      ...config,
      showMessage: config.showMessage !== false,
    });
  }
}

/**
 * 错误处理钩子，用于在组件中捕获和处理错误
 */
export function useErrorHandler() {
  return {
    handleError: (error: any, config?: ErrorHandlerConfig) => {
      return ErrorHandler.handleError(error, config);
    },
    handleApiError: (error: any, config?: ErrorHandlerConfig) => {
      return ErrorHandler.handleApiError(error, config);
    },
    handleValidationError: (error: any, config?: ErrorHandlerConfig) => {
      return ErrorHandler.handleValidationError(error, config);
    },
    handleBusinessError: (error: any, config?: ErrorHandlerConfig) => {
      return ErrorHandler.handleBusinessError(error, config);
    },
  };
}
